import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, UserCheck, GitBranch, Send } from "lucide-react";

const BULK_STAGES = [
  { value: "unassigned",       label: "Unassigned" },
  { value: "analyst_working",  label: "Analyst Working" },
  { value: "attorney_review",  label: "Attorney Review" },
  { value: "gc_review",        label: "GC Review" },
  { value: "vendor_review",    label: "Vendor Review" },
  { value: "legal_approved",   label: "Legal Approved" },
  { value: "in_review",        label: "In Review" },
  { value: "approved",         label: "Approved" },
  { value: "routing",          label: "Routing" },
  { value: "submitted",        label: "Submitted" },
  { value: "draft",            label: "Draft" },
];

/**
 * Compact action bar shown when the caller has ≥1 contract selected. GC /
 * Legal Manager can bulk-reassign (analyst + attorney) or bulk-change stage.
 */
export default function RepoBulkBar({ selection, contracts, onDone, onClear }) {
  const [mode, setMode] = useState(null); // null | "assign" | "stage"
  const [team, setTeam] = useState([]);
  const [analystId, setAnalystId] = useState("");
  const [attorneyId, setAttorneyId] = useState("");
  const [stage, setStage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/users/team").then(r => setTeam(r.data || [])).catch(() => {});
  }, []);

  const ids = Array.from(selection || []);
  if (ids.length === 0) return null;

  const analysts = team.filter(u => u.role === "contract_analyst");
  const attorneys = team.filter(u => u.role === "attorney");

  const doAssign = async () => {
    if (!analystId && !attorneyId) return toast.error("Pick an analyst or attorney");
    setBusy(true);
    try {
      const body = { contract_ids: ids };
      if (analystId) body.analyst_id = analystId;
      if (attorneyId) body.attorney_id = attorneyId;
      const { data } = await api.post("/contracts/bulk-assign", body);
      toast.success(`Reassigned ${data.modified} contract${data.modified === 1 ? "" : "s"}`);
      setMode(null); setAnalystId(""); setAttorneyId("");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

const STAGE_ORDER = [
  "draft","submitted","routing","in_review","unassigned",
  "analyst_working","attorney_review","gc_review","vendor_review","legal_approved","approved",
];

  const doStage = async () => {
    if (!stage) return toast.error("Pick a stage");
    // Guardrail: warn when the picked stage is earlier in the pipeline than the
    // current stage on ANY selected contract (a demotion can wipe reviewer slot
    // progress or push work back onto attorneys who already signed off).
    const targetIdx = STAGE_ORDER.indexOf(stage);
    const demoted = (contracts || []).filter(c => {
      if (!ids.includes(c.id)) return false;
      const curIdx = STAGE_ORDER.indexOf((c.stage || "").toLowerCase());
      return curIdx >= 0 && targetIdx >= 0 && targetIdx < curIdx;
    });
    if (demoted.length > 0) {
      const preview = demoted.slice(0, 3).map(c => `#${c.contract_number} (${(c.stage || "").replace(/_/g, " ")})`).join(", ");
      const more = demoted.length > 3 ? ` and ${demoted.length - 3} more` : "";
      const ok = window.confirm(
        `⚠ ${demoted.length} of ${ids.length} selected contract${demoted.length === 1 ? " is" : "s are"} being moved BACKWARD to "${stage.replace(/_/g, " ")}".\n\n` +
        `${preview}${more}\n\n` +
        `This can wipe reviewer slot progress and push work back onto reviewers who already signed off. Continue?`
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/contracts/bulk-stage", { contract_ids: ids, stage });
      toast.success(`Moved ${data.modified} contract${data.modified === 1 ? "" : "s"} to ${data.stage.replace(/_/g, " ")}`);
      setMode(null); setStage("");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="sticky top-2 z-20 bg-[#005289] text-white rounded-md px-3 py-2 flex items-center gap-2 shadow-lg" data-testid="repo-bulk-bar">
      <span className="text-xs font-bold" data-testid="repo-bulk-count">{ids.length} selected</span>
      {mode === null && (
        <>
          <button onClick={() => setMode("assign")} data-testid="repo-bulk-assign-open"
                  className="ml-2 h-7 px-2 text-xs bg-white/15 hover:bg-white/25 rounded flex items-center gap-1">
            <UserCheck className="w-3 h-3" /> Reassign
          </button>
          <button onClick={() => setMode("stage")} data-testid="repo-bulk-stage-open"
                  className="h-7 px-2 text-xs bg-white/15 hover:bg-white/25 rounded flex items-center gap-1">
            <GitBranch className="w-3 h-3" /> Change stage
          </button>
        </>
      )}
      {mode === "assign" && (
        <>
          <select value={analystId} onChange={e => setAnalystId(e.target.value)}
                  data-testid="repo-bulk-analyst-select"
                  className="h-7 text-xs bg-white text-slate-700 rounded px-2">
            <option value="">— Analyst —</option>
            {analysts.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={attorneyId} onChange={e => setAttorneyId(e.target.value)}
                  data-testid="repo-bulk-attorney-select"
                  className="h-7 text-xs bg-white text-slate-700 rounded px-2">
            <option value="">— Attorney —</option>
            {attorneys.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <button onClick={doAssign} disabled={busy} data-testid="repo-bulk-assign-submit"
                  className="h-7 px-3 text-xs bg-emerald-500 hover:bg-emerald-600 rounded font-semibold flex items-center gap-1 disabled:opacity-50">
            <Send className="w-3 h-3" /> {busy ? "Assigning…" : "Assign"}
          </button>
          <button onClick={() => setMode(null)} className="h-7 px-2 text-xs bg-white/15 rounded">Cancel</button>
        </>
      )}
      {mode === "stage" && (
        <>
          <select value={stage} onChange={e => setStage(e.target.value)}
                  data-testid="repo-bulk-stage-select"
                  className="h-7 text-xs bg-white text-slate-700 rounded px-2">
            <option value="">— New stage —</option>
            {BULK_STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button onClick={doStage} disabled={busy} data-testid="repo-bulk-stage-submit"
                  className="h-7 px-3 text-xs bg-emerald-500 hover:bg-emerald-600 rounded font-semibold flex items-center gap-1 disabled:opacity-50">
            <Send className="w-3 h-3" /> {busy ? "Moving…" : "Move"}
          </button>
          <button onClick={() => setMode(null)} className="h-7 px-2 text-xs bg-white/15 rounded">Cancel</button>
        </>
      )}
      <button onClick={onClear} data-testid="repo-bulk-clear" title="Clear selection"
              className="ml-auto text-white/70 hover:text-white">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
